import { SectionHeader } from '../AssetsPage/SectionHeader';
import { Box, Divider, IconButton, List } from '@mui/material';
import { Close } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { HeaderText } from '../HeaderText';
import { useSearchParams, useMatch } from 'react-router-dom';
import { SHOW_HELP_PARAM, HELP_VIEW_PARAM, HELP_SECTION_PARAM } from './helpParameters';
import { ReportsLearnView } from './ReportsLearnView';
import { MatchAndCompareHelpView, CompareSubView } from './MatchAndCompareHelpView';
import { AssetsUploadView, UploadView } from './AssetsUploadView';
import { BulkDataImportHelpView } from './BulkDataImportHelpView';
import { PATHS } from '@/utils/paths';

const HelpContentContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '600px',
  backgroundColor: theme.palette.background.default,
  borderRadius: '8px',
  overflow: 'hidden',
}));

const HelpContentBody = styled(Box)({
  flex: 1,
  overflow: 'auto',
  padding: '6px 12px',
});

const HeaderContainer = styled(Box)({
  padding: '0 12px',
});

export enum HelpSection {
  Reports = 'reports',
  Upload = 'upload',
  MatchAndCompare = 'match-and-compare',
  Metadata = 'metadata',
}

export const HelpContent = () => {
  const [searchParameters, setSearchParameters] = useSearchParams();

  const isHelpOpen = searchParameters.get(SHOW_HELP_PARAM) === '1';

  const { ASSETS, REPORT, COMPARE, SEARCH, METADATA_BULK_UPLOAD } = PATHS;
  const isAssetsPage = !!useMatch(ASSETS);
  const isReportPage = !!useMatch(REPORT);
  const isComparePage = !!useMatch(COMPARE);
  const isSearchPage = !!useMatch(SEARCH);
  const isMetadataBulkUploadPage = !!useMatch(METADATA_BULK_UPLOAD);

  const getExpandKeyForCurrentPage = (): HelpSection | undefined => {
    if (isMetadataBulkUploadPage) {
      return HelpSection.Metadata;
    }
    if (isAssetsPage) {
      return HelpSection.Upload;
    }
    if (isReportPage) {
      return HelpSection.Reports;
    }
    if (isComparePage || isSearchPage) {
      return HelpSection.MatchAndCompare;
    }
    return undefined;
  };

  if (!isHelpOpen) {
    return;
  }

  const expandParameter = searchParameters.get(HELP_SECTION_PARAM);
  const effectiveSection = expandParameter || getExpandKeyForCurrentPage();

  let viewParameter = searchParameters.get(HELP_VIEW_PARAM);
  if ((isComparePage || isSearchPage) && effectiveSection === HelpSection.MatchAndCompare && !viewParameter) {
    viewParameter = CompareSubView.ComparingParts;
  }

  const createToggleHandler = (sectionKey: string) => () => {
    setSearchParameters(
      previous => {
        const showHelpValue = previous.get(SHOW_HELP_PARAM);
        const newParameters = new URLSearchParams(previous);
        const isCurrentlyOpen = expandParameter === sectionKey;
        if (isCurrentlyOpen) {
          newParameters.set(HELP_SECTION_PARAM, 'none');
        } else {
          newParameters.set(HELP_SECTION_PARAM, sectionKey);
        }
        newParameters.delete(HELP_VIEW_PARAM);
        if (showHelpValue) {
          newParameters.set(SHOW_HELP_PARAM, showHelpValue);
        }

        return newParameters;
      },
      { replace: true }
    );
  };

  const viewSetter = (view: string) => () => {
    setSearchParameters(
      previous => {
        const showHelpValue = previous.get(SHOW_HELP_PARAM);
        const newParameters = new URLSearchParams(previous);
        newParameters.set(HELP_VIEW_PARAM, view);
        if (showHelpValue) {
          newParameters.set(SHOW_HELP_PARAM, showHelpValue);
        }
        return newParameters;
      },
      { replace: true }
    );
  };

  const handleClose = () => {
    setSearchParameters(
      previous => {
        const newParameters = new URLSearchParams(previous);
        newParameters.delete(SHOW_HELP_PARAM);
        newParameters.delete(HELP_VIEW_PARAM);
        newParameters.delete(HELP_SECTION_PARAM);
        return newParameters;
      },
      { replace: true }
    );
  };

  return (
    <HelpContentContainer>
      <HeaderContainer>
        <SectionHeader>
          <HeaderText sx={{ flex: 1 }}>Help</HeaderText>
          <IconButton onClick={handleClose} size='small' sx={{ color: 'text.secondary' }}>
            <Close fontSize='small' />
          </IconButton>
        </SectionHeader>
      </HeaderContainer>
      <Divider />
      <HelpContentBody>
        <List>
          <ReportsLearnView
            isOpen={effectiveSection === HelpSection.Reports}
            onToggle={createToggleHandler(HelpSection.Reports)}
          />
          <MatchAndCompareHelpView
            isOpen={effectiveSection === HelpSection.MatchAndCompare}
            onToggle={createToggleHandler(HelpSection.MatchAndCompare)}
            viewParameter={viewParameter as CompareSubView | null}
            setCompareView={viewSetter}
          />
          <AssetsUploadView
            isOpen={effectiveSection === HelpSection.Upload}
            onToggle={createToggleHandler(HelpSection.Upload)}
            viewParameter={viewParameter as UploadView | null}
            setUploadView={viewSetter}
          />
          <BulkDataImportHelpView
            isOpen={effectiveSection === HelpSection.Metadata}
            onToggle={createToggleHandler(HelpSection.Metadata)}
          />
        </List>
      </HelpContentBody>
    </HelpContentContainer>
  );
};
